import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Activity, LogOut, User as UserIcon, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";
import { walletOf } from "../lib/coins";
import { Avatar } from "./Avatar";
import { ModalitySwitch } from "./ModalitySwitch";
import { Coins } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Base", id: "dashboard" },
  { to: "/medication-game", label: "Meds", id: "med-game" },
  { to: "/ekg-trainer", label: "EKG", id: "ekg" },
  { to: "/daily", label: "Daily", id: "daily" },
  { to: "/flashcards", label: "Cards", id: "flashcards" },
  { to: "/teams", label: "Teams", id: "teams" },
  { to: "/avatars", label: "Shop", id: "avatars" },
  { to: "/profile", label: "Profile", id: "profile" },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { progress, loaded } = useProgress();
  const wallet = walletOf(progress?.stats);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const navClass = ({ isActive }) =>
    `px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors ${
      isActive ? "text-phosphor border-b border-phosphor" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-phosphor/40 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            <div className="relative">
              <Activity className="w-6 h-6 text-phosphor" />
              <div className="absolute inset-0 blur-md bg-primary/40 rounded-full" />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] sm:text-[11px] tracking-[0.3em] text-muted-foreground whitespace-nowrap">GSOFTWARE LLC</div>
              <div className="text-sm font-semibold tracking-widest text-foreground">FIBULA GAMES</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={navClass} data-testid={`nav-${n.id}`}>{n.label}</NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <ModalitySwitch compact className="hidden md:inline-flex" />
            {user !== null && loaded && (
              <Link to="/avatars" data-testid="header-wallet" title="Avatar shop" className="flex items-center gap-2 border border-border hover:border-amber-300 px-2 py-1 transition-colors">
                <Avatar id={wallet.avatar} bg={wallet.bg} size={24} testid="header-avatar" />
                <span className="text-xs font-bold text-amber-300 tabular-nums inline-flex items-center gap-1" data-testid="header-coins"><Coins className="w-3 h-3" /> {wallet.coins}</span>
              </Link>
            )}
            {user && user !== false ? (
              <>
                <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                  <UserIcon className="w-3 h-3" /> {user.name || user.email}
                </div>
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-destructive transition-colors px-3 py-2 border border-border hover:border-destructive/60"
                >
                  <LogOut className="w-3 h-3" />
                  <span className="hidden sm:inline">Log Out</span>
                </button>
              </>
            ) : (
              <>
                {user === false && (
                  <span className="hidden sm:inline text-[10px] tracking-[0.25em] text-muted-foreground" data-testid="guest-badge">GUEST MODE</span>
                )}
                <Link
                  to="/auth"
                  data-testid="header-signin-link"
                  className="text-xs uppercase tracking-widest px-3 sm:px-4 py-2 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Sign In<span className="hidden sm:inline"> to Save</span>
                </Link>
              </>
            )}
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-btn"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="md:hidden p-2 border border-phosphor/30 hover:border-phosphor text-foreground transition-colors"
            >
              {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-phosphor/30 bg-background/95 backdrop-blur-sm" data-testid="mobile-menu">
            <nav className="max-w-7xl mx-auto px-6 py-3 grid grid-cols-2 gap-1">
              <div className="col-span-2 mb-1 flex items-center justify-between gap-3">
                <span className="text-[10px] tracking-[0.3em] text-muted-foreground">MODALITY</span>
                <ModalitySwitch compact />
              </div>
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${n.id}`}
                  className={({ isActive }) =>
                    `px-4 py-3 text-xs tracking-[0.2em] uppercase border ${
                      isActive ? "border-phosphor text-phosphor bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {user && user !== false && (
                <button
                  onClick={handleLogout}
                  data-testid="mobile-logout-btn"
                  className="col-span-2 mt-1 flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground border border-border hover:text-destructive hover:border-destructive/60"
                >
                  <LogOut className="w-3 h-3" /> Log Out
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-phosphor/30 py-6 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] text-muted-foreground tracking-wider" data-testid="footer-disclaimer">
          <div data-testid="footer-disclaimer-text">
            For <span className="text-phosphor">entertainment & study</span> only — not for clinical decision-making.
          </div>
          <div className="opacity-70" data-testid="footer-brand">Built by <span className="text-foreground">GSoftware LLC</span></div>
        </div>
      </footer>
    </div>
  );
}
